export const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      experience: "Experience",
      about: "About",
      fees: "Fees",
      contact: "Contact",
      cta: "Work with me",
    },
    fees: {
      label: "Fees",
      title: "Two levels of dedicated support, designed around the way you work.",
      intro: "Choose the level that best fits your needs. Additional support outside standard hours is available on request.",
      essential: {
        name: "Essential",
        price: "€499",
        per: "/ month",
        hours: "09:00 — 13:00 · Spain time",
        desc: "For executives who need reliable, experienced support during part of their working day.",
      },
      executive: {
        name: "Executive",
        price: "€899",
        per: "/ month",
        hours: "09:00 — 17:00 · Spain time",
        desc: "For executives who want a trusted remote right hand throughout the working day.",
        badge: "Most popular",
      },
      list: [
        "Executive assistance",
        "Personal assistance",
        "Complex travel & itinerary management",
        "Research & coordination",
        "Supplier & provider coordination",
        "Confidential & discreet support",
        "Remote & international support",
      ],
      outside: {
        title: "Outside standard hours",
        text: "Support outside your contracted hours is available at an additional fee, subject to availability.",
        line: "Before 09:00 · 17:00 — 20:00 · 20:00 — 00:00 · Weekends & Public Holidays",
      },
      tailored: {
        label: "Tailored support",
        p1: "Not every executive needs the same level of support.",
        p2: "For clients requiring a lighter, more specific or individually structured arrangement, bespoke monthly support is available.",
        price: "From €399 / month",
      },
      bespoke: {
        title: "Need something different?",
        text: "Every executive has different priorities. Bespoke support arrangements are available for clients with specific schedules, requirements or levels of responsibility.",
      },
      hoursNote: "All standard availability hours are based on Spain time (CET/CEST). International clients are welcome. Time-zone coordination can be arranged when required.",
      taxNote: "Fees are exclusive of applicable taxes.",
    },
    hero: {
      eyebrow: "Executive & Personal Assistant",
      lines: ["Exceptional support", "for exceptional leaders."],
      sub: "Remote executive and personal assistance for CEOs, Presidents, Founders and private executives who value time, discretion and efficiency.",
      stats: ["20+ years of experience", "5 languages", "Remote", "International"],
      ctaPrimary: "Work with me",
      ctaSecondary: "Discover my experience",
      portraitAlt: "Merce Homar — Executive & Personal Assistant",
      portraitCaption: "Merce Homar — Barcelona",
      trust: "Confidentiality · Discretion · Trust",
      trustSub: "Trusted support in environments where privacy, discretion and confidentiality are non-negotiable.",
      trustCred: "Experience supporting high-net-worth individuals, elite athletes, CEOs, senior executives and private families.",
    },
    trust: {
      label: "Confidentiality",
      title: "Trust is the foundation.",
      p1: "When you work behind the scenes of demanding professional and personal lives, discretion isn't an added value. It's a responsibility.",
      p2: "Throughout my career, I have supported high-profile individuals, senior executives and private families, always maintaining the highest standards of confidentiality, discretion and professionalism.",
      words: ["Privacy", "Discretion", "Trust"],
    },
    statement: {
      chapter: "01",
      label: "The Philosophy",
      lines: ["You focus on what matters.", "I take care of the rest."],
      copy: "More than an assistant — a trusted extension of your day. I don't simply complete tasks; I take ownership of the details behind your work and your life.",
    },
    marquee: ["Discretion", "Anticipation", "Ownership", "Precision", "Trust", "Calm authority"],
    services: {
      chapter: "02",
      label: "Services",
      title: "What I can take off your plate.",
      intro: "From executive calendars and complex international travel to personal matters and Family Office support, I take ownership of the details that keep demanding lives running smoothly.",
      explore: "Explore",
      keyService: "Key service",
      items: [
        { num: "01", key: "executive", title: "Executive Assistance", desc: "Calendar management, priorities, meetings, correspondence and day-to-day executive support." },
        { num: "02", key: "personal", title: "Personal Assistance", desc: "Private appointments, personal matters, family logistics and confidential coordination." },
        { num: "03", key: "travel", title: "Executive Travel & Itinerary Management", desc: "Complex domestic and international travel: multi-destination itineraries, constantly changing schedules, last-minute changes, hotels, transfers and private transportation.", tagline: "I don't simply book travel. I manage the entire journey." },
        { num: "04", key: "familyoffice", title: "Family Office Support", desc: "Administrative and personal support for executives and families, including coordination with external providers." },
        { num: "05", key: "other", title: "Global Partner & Supplier Connections", desc: "Connecting Spanish businesses and agencies with trusted international hotels, resorts, venues and specialist suppliers." },
        { num: "06", key: "other", title: "Events & Corporate Coordination", desc: "Executive meetings, conferences, corporate events, suppliers and logistics." },
        { num: "07", key: "other", title: "Research & Problem Solving", desc: "Finding solutions, coordinating providers, handling unexpected situations and taking ownership." },
      ],
    },
    travel: {
      chapter: "03",
      label: "Executive Travel Management",
      title: "Complex travel. Completely handled.",
      copy: "More than twenty years coordinating demanding executive and personal travel — from complex international itineraries to last-minute changes, private transportation and constantly evolving schedules.",
      flow: ["Flights", "Hotels", "Transfers", "Itinerary", "Changes", "Solutions"],
      capabilities: ["Multi-city itineraries", "Last-minute changes", "Private drivers & transfers", "Cancellations, rebooked", "Multiple travelers, one plan", "Executive logistics"],
      statement: "When plans change, I make the plan work.",
    },
    experience: {
      chapter: "04",
      label: "Experience",
      title: "Two decades where decisions move fast.",
      items: [
        { years: "2024 — Present", role: "Executive Assistant to the President", place: "Family Office", desc: "Direct support to the President: complex agenda, personal matters, executive travel and confidential coordination across family office interests." },
        { years: "2022 — 2024", role: "EA to the CFO & PA to the CEO", place: "Wallbox Chargers", desc: "Dual C-level support in a fast-growing tech company: board meetings, investor visits, international travel and constantly changing schedules." },
        { years: "2021 — 2022", role: "Executive Secretary", place: "Servei Català de la Salut", desc: "Executive office support within Catalonia's public health service: documentation, coordination and high-volume institutional scheduling." },
        { years: "2018 — 2020", role: "Executive Assistant & Event Manager", place: "Urbe Events", desc: "Executive assistance combined with corporate event production: suppliers, logistics and demanding live environments." },
        { years: "2014 — 2016", role: "Executive Office Manager", place: "Amilla Resort & Residences · Maldives", desc: "Executive office management at a luxury island resort: international owners, VIP guests and complex travel logistics." },
        { years: "2004 — 2014", role: "Executive Assistant & PA", place: "Roddacom / Rocamora / Cewa", desc: "A decade supporting directors across three companies — the foundation of calendar, travel, correspondence and absolute reliability." },
      ],
      note: "References available upon private request.",
    },
    credentials: [
      { value: "20+", label: "Years of executive experience" },
      { value: "5", label: "Languages" },
      { value: "C-Level", label: "Executive experience" },
      { value: "Complex Travel", label: "Executive & personal itineraries" },
      { value: "Remote", label: "International support" },
    ],
    languages: {
      label: "Languages",
      title: "Five languages. One standard.",
      note: "Multilingual communication allows me to work seamlessly with international executives, families and suppliers.",
      items: [
        { name: "Spanish", level: "C2" },
        { name: "Catalan", level: "C2" },
        { name: "English", level: "C1" },
        { name: "Italian", level: "C1" },
        { name: "French", level: "C1" },
      ],
    },
    about: {
      chapter: "05",
      label: "About",
      title: "The person behind the support.",
      paragraphs: [
        "For more than two decades I have supported presidents, CEOs and CFOs — in family offices, tech companies, luxury hospitality and public institutions — always where decisions move fast.",
        "I'm used to environments where priorities, schedules and travel plans change constantly — and where someone needs to stay calm, think ahead and make everything work.",
      ],
      quote: "You don't need to manage me. You need to trust me.",
      values: ["Discretion", "Reliability", "Organisation", "Anticipation", "Responsibility"],
      signature: "Merce Homar",
      portraitAlt: "Merce Homar portrait",
    },
    style: {
      chapter: "06",
      label: "Working style",
      title: "I take ownership.",
      items: [
        { word: "Discreet", desc: "Confidentiality is fundamental." },
        { word: "Proactive", desc: "I anticipate needs rather than simply wait for instructions." },
        { word: "Resourceful", desc: "When something needs solving, I find the way." },
        { word: "Reliable", desc: "Once I take ownership, I make sure it gets done." },
      ],
    },
    international: {
      eyebrow: "Remote & International",
      title: "Based in Spain. Working worldwide.",
      copy: "Remote executive and personal support for leaders who operate across borders, time zones and cultures.",
      regions: ["Spain", "Europe", "International"],
    },
    contact: {
      chapter: "07",
      label: "Contact",
      lines: ["Your time is valuable.", "Your support should be exceptional."],
      tags: ["Private", "Discreet", "Remote", "International"],
      cta: "Let's talk",
      intro1: "Tell me what you need.",
      intro2: "Whether you need ongoing executive support, personal assistance, complex travel management or help managing the details behind a demanding schedule — let's start a conversation.",
      form: {
        name: "Full name",
        email: "Email address",
        company: "Company",
        position: "Position",
        country: "Country",
        service: "Service required",
        message: "How can I help?",
        submit: "Send message",
        sending: "Sending…",
      },
      services: [
        { key: "executive", label: "Executive Assistance" },
        { key: "personal", label: "Personal Assistance" },
        { key: "travel", label: "Executive Travel" },
        { key: "familyoffice", label: "Family Office Support" },
        { key: "ongoing", label: "Ongoing Remote Support" },
        { key: "other", label: "Other" },
      ],
      privacy: "Every enquiry is treated with strict confidentiality.",
      linkedin: "Connect on LinkedIn",
      success: "Message received. Thank you — I will reply personally.",
      error: "Something went wrong. Please try again.",
    },
    footer: {
      line: "You focus on what matters. I take care of the rest.",
      tagline: "Executive & Personal Assistant",
      rights: "All rights reserved.",
      privacy: "Private · Discreet · Confidential",
      brochure: "Service brochure (PDF)",
    },
    legal: {
      label: "Privacy",
      title: "Privacy Policy",
      updated: "Last updated: 26 August 2026",
      sections: [
        {
          h: "1. Data Controller",
          p: "The person responsible for the processing of personal data collected through this website is: Merce Homar — Executive & Personal Assistant, based in Spain. Email: mercellina@gmail.com",
        },
        {
          h: "2. What Personal Data Is Collected",
          p: "When you contact me through the website, I may collect the information you voluntarily provide, including:",
          items: ["Name", "Email address", "Company", "Position", "Country", "Service of interest", "Message and any other information voluntarily included in your enquiry"],
          p2: "Please do not include sensitive personal information that is not necessary for your enquiry.",
        },
        {
          h: "3. Purpose of Processing",
          p: "Personal data is processed for the following purposes:",
          items: [
            "To receive and respond to enquiries submitted through the contact form.",
            "To provide information about my Executive & Personal Assistance services.",
            "To communicate with you regarding a potential professional relationship.",
            "To manage a professional relationship where one is established.",
            "To comply with applicable legal obligations.",
          ],
          p2: "Personal data submitted through the contact form is not used for unsolicited marketing purposes.",
        },
        {
          h: "4. Legal Basis",
          p: "The legal basis for processing depends on the purpose of the processing and may include:",
          items: [
            "Taking steps at the request of the individual prior to entering into a professional or contractual relationship.",
            "Performance of a contract where a professional relationship is established.",
            "Compliance with legal obligations where applicable.",
            "Consent where consent is specifically required.",
          ],
        },
        {
          h: "5. Data Retention",
          p: "Personal data will be retained only for as long as necessary to handle the enquiry and, where applicable, to manage the professional relationship.",
          p2: "Data may subsequently be retained for the period required to comply with applicable legal obligations or to establish, exercise or defend legal claims.",
        },
        {
          h: "6. Service Providers",
          p: "Personal data may be processed by trusted technology and service providers that are necessary for the operation of the website and professional activity, such as website hosting, email, contact-form, payment, invoicing or other business-support services.",
          p2: "These providers process personal data only as necessary to provide their services and under appropriate contractual and security arrangements. Personal data is not sold or provided to third parties for their own advertising purposes.",
        },
        {
          h: "7. International Transfers",
          p: "Some technology or service providers may process data outside Spain or the European Economic Area.",
          p2: "Where this occurs, appropriate safeguards will be applied in accordance with applicable data protection legislation.",
        },
        {
          h: "8. Your Rights",
          p: "You may, where applicable, exercise your rights to:",
          items: [
            "Access your personal data",
            "Request correction of inaccurate data",
            "Request deletion of your data",
            "Request restriction of processing",
            "Object to certain processing",
            "Request data portability",
            "Withdraw consent where processing is based on consent",
          ],
          p2: "To exercise your rights, please contact: mercellina@gmail.com",
        },
        {
          h: "9. Right to Lodge a Complaint",
          p: "You have the right to lodge a complaint with the Spanish Data Protection Agency (AEPD) if you believe that your personal data has been processed in breach of applicable data protection legislation.",
        },
        {
          h: "10. Confidentiality",
          p: "Given the nature of my professional activity, information received through this website is treated with strict confidentiality and discretion.",
        },
        {
          h: "11. Security",
          p: "Reasonable technical and organisational measures are applied to protect personal data against unauthorised access, loss, alteration or misuse.",
        },
        {
          h: "12. Cookies and Analytics",
          p: "This website uses privacy-focused analytics (PostHog) to understand aggregate, anonymous usage of the site. No advertising cookies or third-party marketing trackers are used.",
        },
        {
          h: "13. Changes to This Privacy Policy",
          p: "This Privacy Policy may be updated from time to time to reflect legal, technical or business changes. The latest version will always be available on this page.",
        },
      ],
    },
    servicePage: {
      back: "All services",
      includes: "What it includes",
      experience: "Relevant experience",
      why: "Why it matters",
      next: "Next service",
    },
    crm: {
      title: "Private enquiries",
      subtitle: "Merce Homar — Lead management",
      passcode: "Access code",
      enter: "Enter",
      notes: "Private notes",
      save: "Save notes",
      saved: "Saved",
      empty: "No enquiries yet.",
      back: "Back to website",
      refresh: "Refresh",
    },
  },
  es: {
    nav: {
      home: "Inicio",
      services: "Servicios",
      experience: "Experiencia",
      about: "Sobre mí",
      fees: "Honorarios",
      contact: "Contacto",
      cta: "Trabaja conmigo",
    },
    fees: {
      label: "Honorarios",
      title: "Dos niveles de soporte dedicado, diseñados en torno a tu forma de trabajar.",
      intro: "Elige el nivel que mejor encaje con tus necesidades. Soporte adicional fuera del horario estándar disponible bajo petición.",
      essential: {
        name: "Essential",
        price: "€499",
        per: "/ mes",
        hours: "09:00 — 13:00 · hora de España",
        desc: "Para directivos que necesitan soporte fiable y con experiencia durante parte de su jornada.",
      },
      executive: {
        name: "Executive",
        price: "€899",
        per: "/ mes",
        hours: "09:00 — 17:00 · hora de España",
        desc: "Para directivos que quieren una mano derecha en remoto de confianza durante toda la jornada.",
        badge: "El más elegido",
      },
      list: [
        "Asistencia ejecutiva",
        "Asistencia personal",
        "Gestión de viajes complejos e itinerarios",
        "Investigación y coordinación",
        "Coordinación de proveedores",
        "Soporte confidencial y discreto",
        "Soporte remoto e internacional",
      ],
      outside: {
        title: "Fuera del horario estándar",
        text: "El soporte fuera de tu horario contratado está disponible con un coste adicional, sujeto a disponibilidad.",
        line: "Antes de las 09:00 · 17:00 — 20:00 · 20:00 — 00:00 · Fines de semana y festivos",
      },
      tailored: {
        label: "Soporte a medida",
        p1: "No todos los directivos necesitan el mismo nivel de soporte.",
        p2: "Para clientes que requieren un acuerdo más ligero, específico o estructurado individualmente, existe soporte mensual a medida.",
        price: "Desde €399 / mes",
      },
      bespoke: {
        title: "¿Necesitas algo diferente?",
        text: "Cada directivo tiene prioridades diferentes. Los acuerdos de soporte a medida están disponibles para clientes con horarios, requisitos o niveles de responsabilidad específicos.",
      },
      hoursNote: "Todo el horario de disponibilidad estándar se indica en hora de España (CET/CEST). Los clientes internacionales son bienvenidos. La coordinación entre zonas horarias se puede organizar cuando sea necesario.",
      taxNote: "Los honorarios no incluyen los impuestos aplicables.",
    },
    hero: {
      eyebrow: "Asistente Ejecutiva y Personal",
      lines: ["Soporte excepcional", "para líderes excepcionales."],
      sub: "Asistencia ejecutiva y personal en remoto para CEOs, presidentes, fundadores y ejecutivos que valoran el tiempo, la discreción y la eficiencia.",
      stats: ["Más de 20 años de experiencia", "5 idiomas", "En remoto", "Internacional"],
      ctaPrimary: "Trabaja conmigo",
      ctaSecondary: "Descubre mi experiencia",
      portraitAlt: "Merce Homar — Asistente Ejecutiva y Personal",
      portraitCaption: "Merce Homar — Barcelona",
      trust: "Confidencialidad · Discreción · Confianza",
      trustSub: "Soporte de confianza en entornos donde la privacidad, la discreción y la confidencialidad son innegociables.",
      trustCred: "Experiencia apoyando a personas de alto patrimonio, deportistas de élite, CEOs, altos directivos y familias privadas.",
    },
    trust: {
      label: "Confidencialidad",
      title: "La confianza es la base.",
      p1: "Cuando trabajas entre bastidores de vidas profesionales y personales exigentes, la discreción no es un valor añadido. Es una responsabilidad.",
      p2: "A lo largo de mi carrera he apoyado a personas de alto perfil, altos directivos y familias privadas, manteniendo siempre los más altos estándares de confidencialidad, discreción y profesionalidad.",
      words: ["Privacidad", "Discreción", "Confianza"],
    },
    statement: {
      chapter: "01",
      label: "La Filosofía",
      lines: ["Tú céntrate en lo importante.", "Yo me encargo del resto."],
      copy: "Más que una asistente: una extensión de confianza de tu día a día. No me limito a completar tareas; asumo la responsabilidad de los detalles de tu trabajo y tu vida.",
    },
    marquee: ["Discreción", "Anticipación", "Responsabilidad", "Precisión", "Confianza", "Autoridad serena"],
    services: {
      chapter: "02",
      label: "Servicios",
      title: "Lo que puedo quitar de tu agenda.",
      intro: "Desde agendas ejecutivas y viajes internacionales complejos hasta asuntos personales y soporte a Family Office: asumo la responsabilidad de los detalles que mantienen en marcha vidas exigentes.",
      explore: "Explorar",
      keyService: "Servicio clave",
      items: [
        { num: "01", key: "executive", title: "Asistencia Ejecutiva", desc: "Gestión de agenda, prioridades, reuniones, correspondencia y soporte ejecutivo del día a día." },
        { num: "02", key: "personal", title: "Asistencia Personal", desc: "Citas privadas, asuntos personales, logística familiar y coordinación confidencial." },
        { num: "03", key: "travel", title: "Viajes Ejecutivos y Gestión de Itinerarios", desc: "Viajes nacionales e internacionales complejos: itinerarios multidestino, agendas en constante cambio, imprevistos de última hora, hoteles, traslados y transporte privado.", tagline: "No me limito a reservar viajes. Gestiono el viaje completo." },
        { num: "04", key: "familyoffice", title: "Soporte a Family Office", desc: "Soporte administrativo y personal para directivos y familias, incluida la coordinación con proveedores externos." },
        { num: "05", key: "other", title: "Conexiones Globales de Partners y Proveedores", desc: "Conecto empresas y agencias españolas con hoteles, resorts, espacios y proveedores especializados internacionales de confianza." },
        { num: "06", key: "other", title: "Eventos y Coordinación Corporativa", desc: "Reuniones ejecutivas, conferencias, eventos corporativos, proveedores y logística." },
        { num: "07", key: "other", title: "Investigación y Soluciones", desc: "Encontrar soluciones, coordinar proveedores, gestionar imprevistos y asumir la responsabilidad." },
      ],
    },
    travel: {
      chapter: "03",
      label: "Gestión de Viajes Ejecutivos",
      title: "Viajes complejos. Completamente gestionados.",
      copy: "Más de veinte años coordinando viajes ejecutivos y personales exigentes: itinerarios internacionales complejos, cambios de última hora, transporte privado y agendas en constante evolución.",
      flow: ["Vuelos", "Hoteles", "Traslados", "Itinerario", "Cambios", "Soluciones"],
      capabilities: ["Itinerarios multiciudad", "Cambios de última hora", "Conductores privados y traslados", "Cancelaciones gestionadas", "Varios viajeros, un solo plan", "Logística ejecutiva"],
      statement: "Cuando los planes cambian, yo hago que el plan funcione.",
    },
    experience: {
      chapter: "04",
      label: "Experiencia",
      title: "Dos décadas donde las decisiones se mueven rápido.",
      items: [
        { years: "2024 — Hoy", role: "Asistente Ejecutiva del Presidente", place: "Family Office", desc: "Soporte directo al Presidente: agenda compleja, asuntos personales, viajes ejecutivos y coordinación confidencial de los intereses del family office." },
        { years: "2022 — 2024", role: "EA del CFO y PA del CEO", place: "Wallbox Chargers", desc: "Doble soporte C-Level en una empresa tecnológica en pleno crecimiento: consejos, visitas de inversores, viajes internacionales y agendas en constante cambio." },
        { years: "2021 — 2022", role: "Secretaria Ejecutiva", place: "Servei Català de la Salut", desc: "Soporte de oficina ejecutiva en el servicio público de salud de Cataluña: documentación, coordinación y agenda institucional de alto volumen." },
        { years: "2018 — 2020", role: "Asistente Ejecutiva y Event Manager", place: "Urbe Events", desc: "Asistencia ejecutiva combinada con producción de eventos corporativos: proveedores, logística y entornos exigentes en directo." },
        { years: "2014 — 2016", role: "Executive Office Manager", place: "Amilla Resort & Residences · Maldivas", desc: "Gestión de oficina ejecutiva en un resort de lujo: propietarios internacionales, huéspedes VIP y logística de viajes compleja." },
        { years: "2004 — 2014", role: "Asistente Ejecutiva y PA", place: "Roddacom / Rocamora / Cewa", desc: "Una década apoyando a la dirección en tres empresas: la base de agenda, viajes, correspondencia y fiabilidad absoluta." },
      ],
      note: "Referencias disponibles bajo petición privada.",
    },
    credentials: [
      { value: "20+", label: "Años de experiencia ejecutiva" },
      { value: "5", label: "Idiomas" },
      { value: "C-Level", label: "Experiencia ejecutiva" },
      { value: "Viajes Complejos", label: "Itinerarios ejecutivos y personales" },
      { value: "Remoto", label: "Soporte internacional" },
    ],
    languages: {
      label: "Idiomas",
      title: "Cinco idiomas. Un mismo estándar.",
      note: "La comunicación multilingüe me permite trabajar con fluidez con directivos, familias y proveedores internacionales.",
      items: [
        { name: "Español", level: "C2" },
        { name: "Catalán", level: "C2" },
        { name: "Inglés", level: "C1" },
        { name: "Italiano", level: "C1" },
        { name: "Francés", level: "C1" },
      ],
    },
    about: {
      chapter: "05",
      label: "Sobre mí",
      title: "La persona detrás del soporte.",
      paragraphs: [
        "Durante más de dos décadas he apoyado a presidentes, CEOs y CFOs — en family offices, empresas tecnológicas, hostelería de lujo e instituciones públicas — siempre donde las decisiones se mueven rápido.",
        "Estoy acostumbrada a entornos donde las prioridades, las agendas y los viajes cambian constantemente, y donde alguien tiene que mantener la calma, anticiparse y hacer que todo funcione.",
      ],
      quote: "No necesitas gestionarme. Necesitas confiar en mí.",
      values: ["Discreción", "Fiabilidad", "Organización", "Anticipación", "Responsabilidad"],
      signature: "Merce Homar",
      portraitAlt: "Retrato de Merce Homar",
    },
    style: {
      chapter: "06",
      label: "Forma de trabajar",
      title: "Asumo la responsabilidad.",
      items: [
        { word: "Discreta", desc: "La confidencialidad es fundamental." },
        { word: "Proactiva", desc: "Anticipo necesidades en lugar de esperar instrucciones." },
        { word: "Resolutiva", desc: "Cuando algo necesita solución, encuentro el camino." },
        { word: "Fiable", desc: "Cuando asumo algo, me aseguro de que se haga." },
      ],
    },
    international: {
      eyebrow: "En remoto e Internacional",
      title: "Con base en España. Trabajando en todo el mundo.",
      copy: "Soporte ejecutivo y personal en remoto para líderes que operan entre fronteras, zonas horarias y culturas.",
      regions: ["España", "Europa", "Internacional"],
    },
    contact: {
      chapter: "07",
      label: "Contacto",
      lines: ["Tu tiempo es valioso.", "Tu soporte debe ser excepcional."],
      tags: ["Privado", "Discreto", "En remoto", "Internacional"],
      cta: "Hablemos",
      intro1: "Cuéntame qué necesitas.",
      intro2: "Tanto si necesitas soporte ejecutivo continuo, asistencia personal, gestión de viajes complejos o ayuda con los detalles de una agenda exigente — empecemos una conversación.",
      form: {
        name: "Nombre completo",
        email: "Correo electrónico",
        company: "Empresa",
        position: "Cargo",
        country: "País",
        service: "Servicio requerido",
        message: "¿Cómo puedo ayudarte?",
        submit: "Enviar mensaje",
        sending: "Enviando…",
      },
      services: [
        { key: "executive", label: "Asistencia Ejecutiva" },
        { key: "personal", label: "Asistencia Personal" },
        { key: "travel", label: "Viajes Ejecutivos" },
        { key: "familyoffice", label: "Soporte a Family Office" },
        { key: "ongoing", label: "Soporte Remoto Continuo" },
        { key: "other", label: "Otro" },
      ],
      privacy: "Cada consulta se trata con estricta confidencialidad.",
      linkedin: "Conecta en LinkedIn",
      success: "Mensaje recibido. Gracias — te responderé personalmente.",
      error: "Algo salió mal. Inténtalo de nuevo.",
    },
    footer: {
      line: "Tú céntrate en lo importante. Yo me encargo del resto.",
      tagline: "Asistente Ejecutiva y Personal",
      rights: "Todos los derechos reservados.",
      privacy: "Privado · Discreto · Confidencial",
      brochure: "Dosier de servicios (PDF)",
    },
    legal: {
      label: "Privacidad",
      title: "Política de Privacidad",
      updated: "Última actualización: 26 de agosto de 2026",
      sections: [
        {
          h: "1. Responsable del tratamiento",
          p: "La responsable del tratamiento de los datos personales recogidos a través de este sitio web es: Merce Homar — Asistente Ejecutiva y Personal, con residencia profesional en España. Email: mercellina@gmail.com",
        },
        {
          h: "2. Qué datos personales se recogen",
          p: "Cuando me contactas a través del sitio web, puedo recoger la información que facilitas voluntariamente, incluyendo:",
          items: ["Nombre", "Correo electrónico", "Empresa", "Cargo", "País", "Servicio de interés", "Mensaje y cualquier otra información incluida voluntariamente en tu consulta"],
          p2: "Por favor, no incluyas información personal sensible que no sea necesaria para tu consulta.",
        },
        {
          h: "3. Finalidad del tratamiento",
          p: "Los datos personales se tratan con las siguientes finalidades:",
          items: [
            "Recibir y responder a las consultas enviadas a través del formulario de contacto.",
            "Facilitar información sobre mis servicios de Asistencia Ejecutiva y Personal.",
            "Comunicarme contigo en relación con una posible relación profesional.",
            "Gestionar la relación profesional cuando esta se establezca.",
            "Cumplir con las obligaciones legales aplicables.",
          ],
          p2: "Los datos personales enviados a través del formulario de contacto no se utilizan con fines de marketing no solicitado.",
        },
        {
          h: "4. Base legal",
          p: "La base legal del tratamiento depende de su finalidad y puede incluir:",
          items: [
            "La aplicación de medidas a petición del interesado previas a una relación profesional o contractual.",
            "La ejecución de un contrato cuando se establece una relación profesional.",
            "El cumplimiento de obligaciones legales cuando resulte aplicable.",
            "El consentimiento, cuando este sea específicamente requerido.",
          ],
        },
        {
          h: "5. Conservación de los datos",
          p: "Los datos personales se conservarán únicamente durante el tiempo necesario para gestionar la consulta y, en su caso, para gestionar la relación profesional.",
          p2: "Posteriormente, los datos podrán conservarse durante el período requerido para cumplir con obligaciones legales aplicables o para establecer, ejercer o defender reclamaciones legales.",
        },
        {
          h: "6. Proveedores de servicios",
          p: "Los datos personales pueden ser tratados por proveedores tecnológicos y de servicios de confianza necesarios para el funcionamiento del sitio web y de la actividad profesional, como servicios de alojamiento web, correo electrónico, formulario de contacto, facturación u otros servicios de soporte empresarial.",
          p2: "Estos proveedores tratan los datos personales únicamente en la medida necesaria para prestar sus servicios y bajo los acuerdos contractuales y de seguridad adecuados. Los datos personales no se venden ni se ceden a terceros para sus propios fines publicitarios.",
        },
        {
          h: "7. Transferencias internacionales",
          p: "Algunos proveedores tecnológicos o de servicios pueden tratar datos fuera de España o del Espacio Económico Europeo.",
          p2: "Cuando esto ocurra, se aplicarán las garantías adecuadas de conformidad con la legislación de protección de datos aplicable.",
        },
        {
          h: "8. Tus derechos",
          p: "Cuando resulte aplicable, puedes ejercer tus derechos a:",
          items: [
            "Acceder a tus datos personales",
            "Solicitar la rectificación de datos inexactos",
            "Solicitar la supresión de tus datos",
            "Solicitar la limitación del tratamiento",
            "Oponerte a determinados tratamientos",
            "Solicitar la portabilidad de tus datos",
            "Retirar el consentimiento cuando el tratamiento se base en él",
          ],
          p2: "Para ejercer tus derechos, escribe a: mercellina@gmail.com",
        },
        {
          h: "9. Derecho a presentar una reclamación",
          p: "Tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD) si consideras que tus datos personales han sido tratados infringiendo la legislación aplicable.",
        },
        {
          h: "10. Confidencialidad",
          p: "Dada la naturaleza de mi actividad profesional, la información recibida a través de este sitio web se trata con estricta confidencialidad y discreción.",
        },
        {
          h: "11. Seguridad",
          p: "Se aplican medidas técnicas y organizativas razonables para proteger los datos personales frente a accesos no autorizados, pérdida, alteración o uso indebido.",
        },
        {
          h: "12. Cookies y analítica",
          p: "Este sitio web utiliza analítica enfocada en la privacidad (PostHog) para comprender el uso agregado y anónimo del sitio. No se utilizan cookies publicitarias ni rastreadores de marketing de terceros.",
        },
        {
          h: "13. Cambios en esta política de privacidad",
          p: "Esta Política de Privacidad puede actualizarse ocasionalmente para reflejar cambios legales, técnicos o de actividad. La versión más reciente estará siempre disponible en esta página.",
        },
      ],
    },
    servicePage: {
      back: "Todos los servicios",
      includes: "Qué incluye",
      experience: "Experiencia relevante",
      why: "Por qué importa",
      next: "Siguiente servicio",
    },
    crm: {
      title: "Consultas privadas",
      subtitle: "Merce Homar — Gestión de contactos",
      passcode: "Código de acceso",
      enter: "Entrar",
      notes: "Notas privadas",
      save: "Guardar notas",
      saved: "Guardado",
      empty: "Aún no hay consultas.",
      back: "Volver a la web",
      refresh: "Actualizar",
    },
  },
};
